import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Scene } from '@babylonjs/core/scene';
import { EntityFlag } from '@fisura/protocol';
import { TEAM_COLOR } from '@fisura/shared';
import { Character, loadHumanoidContainer } from './character.js';
import type { RenderEntity } from './net.js';

/**
 * Renders each remote player as an animated, skinned humanoid driven by the
 * snapshot stream. The rigged model loads asynchronously; until it's ready,
 * update() is a no-op (characters appear a beat after the asset loads).
 */
export class RemoteRenderer {
  private readonly pool = new Map<number, Character>();
  private readonly corpses: Character[] = [];
  private readonly stride = new Map<number, number>();
  private container: AssetContainer | null = null;

  constructor(
    private readonly scene: Scene,
    /** Called when a remote takes a footstep (for positional audio). */
    private readonly onFootstep?: (x: number, z: number) => void,
  ) {
    void loadHumanoidContainer(scene).then((c) => {
      this.container = c;
    });
  }

  update(
    entities: RenderEntity[],
    selfId: number | null,
    dt: number,
    selfTeam = -1,
    nameOf?: (id: number) => string,
  ): void {
    if (!this.container) return;
    const seen = new Set<number>();

    for (const e of entities) {
      if (e.id === selfId) continue; // first person: never render our own body
      const alive = (e.flags & EntityFlag.Alive) !== 0;

      if (!alive) {
        // Death transition: hand the body to the corpse list (falls + fades)
        // instead of popping it out of existence.
        const ch = this.pool.get(e.id);
        if (ch) {
          ch.beginDeath();
          this.corpses.push(ch);
          this.pool.delete(e.id);
          this.stride.delete(e.id);
        }
        continue;
      }
      seen.add(e.id);

      let ch = this.pool.get(e.id);
      if (!ch) {
        const color = TEAM_COLOR[e.teamId] ?? [0.8, 0.8, 0.8];
        ch = new Character(this.scene, this.container, color);
        this.pool.set(e.id, ch);
      }
      ch.update(e, dt);

      // Teammates get a floating name tag (auto-refreshes when the roster lands).
      if (nameOf && e.teamId === selfTeam) {
        ch.setNameTag(nameOf(e.id), TEAM_COLOR[e.teamId] ?? [0.8, 0.8, 0.8]);
      }

      // Footsteps: accumulate stride distance while moving on the ground.
      if (this.onFootstep && (e.flags & EntityFlag.OnGround) !== 0) {
        const speed = Math.hypot(e.vx, e.vz);
        if (speed > 1.2) {
          const acc = (this.stride.get(e.id) ?? 0) + speed * dt;
          if (acc >= 2.6) {
            this.stride.set(e.id, 0);
            this.onFootstep(e.x, e.z);
          } else {
            this.stride.set(e.id, acc);
          }
        }
      }
    }

    // Disconnected players (no entity at all) just despawn.
    for (const [id, ch] of this.pool) {
      if (!seen.has(id)) {
        ch.dispose();
        this.pool.delete(id);
        this.stride.delete(id);
      }
    }

    // Advance corpse falls/fades; dispose when fully gone.
    for (let i = this.corpses.length - 1; i >= 0; i--) {
      if (!this.corpses[i]!.updateCorpse()) {
        this.corpses[i]!.dispose();
        this.corpses.splice(i, 1);
      }
    }
  }
}
